import AddIcon from '@mui/icons-material/Add'
import { Stack, Button, CircularProgress, Box } from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import {
  useCreateProjectDocumentRule,
  useDeleteProjectDocumentRule,
  useGetProjectDocumentRules,
  useUpdateProjectDocumentRules,
} from '@/service-library/hooks/project-document-rules'
import { ProjectDocumentRule } from '@/types/rules'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import { DraggableList } from '@/components/lists/DraggableList'
import RuleCard from '@/components/rules/RuleCard'
import AddDocumentRuleDialog from './AddDocumentRuleDialog'

type DocumentRulesProps = {
  documentRulesQuery: ReturnType<typeof useGetProjectDocumentRules>
  selectedRuleId: string | undefined
  setSelectedRuleId: (ruleId: string | undefined) => void
}

export default function DocumentRules({
  documentRulesQuery,
  selectedRuleId,
  setSelectedRuleId,
}: DocumentRulesProps) {
  const { projectDocumentRules = [], isPending, queryKey } = documentRulesQuery

  const { createProjectDocumentRule } = useCreateProjectDocumentRule({
    listQueryKey: queryKey,
    onMutate: (newDocumentRule) => {
      setSelectedRuleId(newDocumentRule.id)
    },
    onSuccess: () => {
      showSuccessSnackbar('Document Rule Created')
    },
    onError: () => {
      showErrorSnackbar(
        'Failed to create document rule. Please try again later.',
      )
      setSelectedRuleId(undefined)
    },
  })

  const { deleteProjectDocumentRule } = useDeleteProjectDocumentRule({
    listQueryKey: queryKey,
    onMutate: () => {
      setSelectedRuleId(undefined)
    },
    onError: () => {
      showErrorSnackbar('Failed to delete field rule. Please try again later.')
    },
  })

  const { updateProjectDocumentRules } = useUpdateProjectDocumentRules({
    listQueryKey: queryKey,
    useSortOrder: true,
    onMutate: (updatedDocRules) => {
      if (selectedRuleId) {
        const updatedSelectedRule = updatedDocRules.find(
          ({ id }) => selectedRuleId === id,
        )
        if (updatedSelectedRule) {
          setSelectedRuleId(updatedSelectedRule.id)
        }
      }
    },
  })

  const reorderDocumentRules = (
    reorderedDocumentRules: ProjectDocumentRule[],
  ) => {
    const sortedDocumentRules = reorderedDocumentRules.map((item, index) => ({
      ...item,
      sort_order: index,
    }))
    updateProjectDocumentRules(sortedDocumentRules)
  }

  const addRuleOverlay = useOverlay()

  return (
    <Stack spacing={4}>
      {isPending && (
        <Box sx={{ p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      {!isPending && (
        <>
          {/* MARK: Document Rules */}
          <Stack spacing={1}>
            <Box>
              {projectDocumentRules.length > 0 && (
                <DraggableList
                  data={projectDocumentRules}
                  onChange={reorderDocumentRules}
                >
                  {projectDocumentRules.map((documentRule) => (
                    <RuleCard
                      key={documentRule.id}
                      isSelected={selectedRuleId === documentRule.id}
                      onClick={() => {
                        setSelectedRuleId(documentRule.id)
                      }}
                      onDelete={() => {
                        deleteProjectDocumentRule(documentRule.id)
                      }}
                      name={documentRule.type?.name || ''}
                      description={documentRule.type?.description || ''}
                      funcName={documentRule.params.function_name}
                      enabled={documentRule.is_enabled}
                      onEnableDisable={(checked) => {
                        updateProjectDocumentRules([
                          { ...documentRule, is_enabled: checked },
                        ])
                      }}
                    />
                  ))}
                </DraggableList>
              )}
            </Box>

            <div>
              <Button
                variant="text"
                startIcon={<AddIcon />}
                onClick={addRuleOverlay.open}
              >
                Add Rule
              </Button>
              <AddDocumentRuleDialog
                overlay={addRuleOverlay}
                onSubmit={createProjectDocumentRule}
              />
            </div>
          </Stack>
        </>
      )}
    </Stack>
  )
}
